/**
 * =============================================================================
 * WlBillingByPaymentMethodCard
 *
 * Card da aba "Cobrança" (WL Financeiro) exibindo a distribuição das
 * parcelas em aberto por meio de pagamento (donut). Reaproveita o mesmo
 * componente visual usado em "Distribuição do MRR por plano" da aba Visão
 * geral (`WlDistributionDonut`).
 *
 * Base dos dados = mesma do aging (BLOCO 3 da RPC `wl_get_billing_kpis`):
 * parcelas com status `pendente | aguardando | vencido` do tenant master —
 * independente do filtro de período do topo da aba.
 *
 * @version 1.0.0
 * RELEASE:
 *   2026-07-22 v1.0.0 - Versão inicial.
 * =============================================================================
 */
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import { WlDistributionDonut } from './charts/WlDistributionDonut';
import type { WlBillingKpisPayload } from '@/hooks/wl/useWlBillingKpis';

interface Props {
  data?: WlBillingKpisPayload;
  isLoading?: boolean;
}

export function WlBillingByPaymentMethodCard({ data, isLoading }: Props) {
  if (isLoading || !data) {
    // Mesma altura do card de aging para alinhar visualmente na linha.
    return <Skeleton className="h-[380px] w-full" />;
  }

  // Mapeia payload da RPC para o formato genérico do donut.
  const items = (data.payment_methods ?? []).map((r) => ({
    id: r.meio_pagamento_id ?? 'none',
    name: r.nome,
    value: Number(r.total || 0),
  }));

  return (
    <Card className="p-6 space-y-4 h-full">
      {/* Header — segue o padrão do WlBillingAgingCard. */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <span>Cobranças por meio de pagamento</span>
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label="Ajuda: Cobranças por meio de pagamento"
                    className="inline-flex"
                  >
                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/60 hover:text-muted-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs">
                  Distribuição por meio de pagamento das parcelas em aberto
                  (pendente / aguardando / vencido) do tenant master —
                  <b> mesma base do aging</b>. Independente do filtro de
                  período do topo. Cobranças sem meio definido são agrupadas
                  em "Sem meio definido".
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      {/* Donut + legenda */}
      <WlDistributionDonut data={items} height={280} />
    </Card>
  );
}
